import { useSubscription } from "@apollo/client";
import { get, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { KEYS, SCREENS } from "utils/constants";

export const Context = React.createContext("store");
export const { Consumer } = Context;

export const Provider = ({ children }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation(NAME_SPACES.HOME);
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem(KEYS.ACCESS_TOKEN),
  );
  const [user, setUser] = useState(JSON.parse(localStorage.getItem(KEYS.USER)));
  const [language, setLanguage] = useState(localStorage.getItem(KEYS.LANGUAGE));

  const clearStore = () => {
    localStorage.clear();
    setAccessToken("");
    setUser(undefined);
    setLanguage("");
  };

  useEffect(() => {
    const handleStorageChange = (event) => {
      const { key, newValue } = event;

      switch (key) {
        case KEYS.ACCESS_TOKEN:
          setAccessToken(newValue);
          break;

        case KEYS.USER:
          try {
            setUser(newValue ? JSON.parse(newValue) : undefined);
          } catch (error) {
            console.error("Error parsing user data from localStorage:", error);
            setUser(undefined);
          }
          break;

        case KEYS.LANGUAGE:
          setLanguage(newValue);
          if (newValue) {
            i18n.changeLanguage(newValue);
          }
          break;

        default:
          break;
      }

      if (event.key === null) {
        setAccessToken("");
        setUser(undefined);
        setLanguage("");
      } else if (event.key && newValue === null) {
        switch (event.key) {
          case KEYS.ACCESS_TOKEN:
            setAccessToken("");
            break;
          case KEYS.USER:
            setUser(undefined);
            break;
          case KEYS.LANGUAGE:
            setLanguage("");
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [i18n]);

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useSubscription(graphql.subscriptions.USER_UPDATED, {
    onData: ({ data: { data } }) => {
      if (get(data, "userUpdated.isActive")) return;
      const updatedUser = get(data, "userUpdated.id");
      if (updatedUser && updatedUser === user?.id) {
        clearStore();
        navigate(SCREENS.AUTH.SIGN_IN.path, { replace: true });
      }
    },
  });

  const handleSetAccessToken = (data) => {
    setAccessToken(data.accessToken);
    return localStorage.setItem(KEYS.ACCESS_TOKEN, data.accessToken);
  };

  const handleSetUser = (data) => {
    setUser(get(data, "user") || {});
    return isEmpty(get(data, "user"))
      ? localStorage.removeItem(KEYS.USER)
      : localStorage.setItem(KEYS.USER, JSON.stringify(data.user));
  };

  const handleSetLanguage = (data) => {
    setLanguage(data.language);
    return localStorage.setItem(KEYS.LANGUAGE, data.language);
  };

  return (
    <Context.Provider
      value={{
        clearStore,
        setAccessToken: handleSetAccessToken,
        accessToken,
        setUser: handleSetUser,
        user,
        setLanguage: handleSetLanguage,
        language,
      }}
    >
      {children}
    </Context.Provider>
  );
};
